import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  ImageOff,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import ShareButton from "components/ShareButton";

/**
 * Grade de fotos da area publica e o lightbox que a acompanha.
 *
 * Usada pela galeria do evento e pelo resultado da busca por selfie na pagina
 * principal — nesta, cada foto vem com `event_name` e o lightbox oferece o
 * caminho ate a galeria do evento de onde ela veio.
 */

// Quantas fotos antes do fim do que ja foi carregado o lightbox dispara a
// proxima pagina — navegar de seta em seta nao pode esbarrar no fim do lote.
const LIGHTBOX_PREFETCH = 5;

// Reenvio de imagem que falhou.
//
// O proxy de borda limita a taxa de requisicoes: numa rolagem rapida uma parte
// das miniaturas volta 503 (o arquivo esta la — sozinha, a mesma URL responde
// 200). Um `<img>` quebrado nunca tenta de novo por conta propria, entao a foto
// ficava faltando ate o visitante recarregar a pagina.
//
// A espera cresce a cada tentativa e leva um sorteio (jitter): o lote que
// falhou junto nao pode voltar junto, senao esbarra no mesmo limite.
const IMG_MAX_ATTEMPTS = 5;
const IMG_BACKOFF_MS = [600, 1500, 3500, 7000];
const IMG_JITTER_MS = 700;

function useRetryableImage(src) {
  const [tries, setTries] = useState(0);
  const [failed, setFailed] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    setTries(0);
    setFailed(false);
  }, [src]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const retry = useCallback(() => {
    clearTimeout(timer.current);
    setFailed(false);
    setTries((n) => n + 1);
  }, []);

  const onError = () => {
    if (tries + 1 >= IMG_MAX_ATTEMPTS) {
      setFailed(true);
      return;
    }
    const step = Math.min(tries, IMG_BACKOFF_MS.length - 1);
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => setTries((n) => n + 1),
      IMG_BACKOFF_MS[step] + Math.random() * IMG_JITTER_MS
    );
  };

  // A URL tem de MUDAR a cada tentativa: reatribuir o mesmo `src` nao refaz o
  // pedido — o browser mantem o resultado da falha.
  const url =
    !src || tries === 0
      ? src
      : `${src}${src.includes("?") ? "&" : "?"}retry=${tries}`;

  return { url, failed, onError, retry };
}

/** Um quadrado da grade: a foto ou, se ela nao vier, o convite a tentar de novo. */
export function PhotoTile({ photo, onOpen, t }) {
  const { url, failed, onError, retry } = useRetryableImage(
    photo.thumbnail_url || photo.image_url
  );

  if (failed) {
    return (
      <div className="flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-3 text-center">
        <ImageOff size={20} className="text-neutral-300" />
        <span className="w-full truncate text-[11px] text-neutral-400">
          {photo.original_name}
        </span>
        <button
          type="button"
          onClick={retry}
          className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-600 transition-colors hover:bg-neutral-50"
        >
          <RefreshCw size={11} />
          {t("common.retry")}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative aspect-square overflow-hidden rounded-xl bg-neutral-100"
    >
      <img
        src={url}
        alt={photo.original_name || ""}
        loading="lazy"
        onError={onError}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      {photo.similarity != null && (
        <span className="absolute bottom-2 left-2 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-semibold text-white">
          {Math.round(photo.similarity * 100)}%
        </span>
      )}
      {/* Busca em todos os eventos: o card diz de que evento a foto e. */}
      {photo.event_name && (
        <span className="absolute inset-x-0 top-0 truncate bg-gradient-to-b from-black/70 to-transparent px-2 pb-4 pt-1.5 text-left text-[11px] font-semibold text-white">
          {photo.event_name}
        </span>
      )}
    </button>
  );
}

// Deslocamento horizontal minimo (px) para o gesto contar como troca de foto.
// Abaixo disso e toque parado; e o movimento tem de ser mais horizontal que
// vertical, senao a rolagem da pagina viraria navegacao sem querer.
const SWIPE_THRESHOLD = 50;

export function Lightbox({
  photos,
  index,
  onClose,
  onNavigate,
  hasMore,
  onNeedMore,
  t,
}) {
  const photo = photos[index];
  const touchStart = useRef(null);
  const atEnd = index >= photos.length - 1;
  const full = useRetryableImage(photo.image_url || photo.thumbnail_url);

  // Navegar de seta em seta tambem consome o album: chegando perto do fim do
  // que ja foi carregado, a proxima pagina e buscada sem fechar o lightbox.
  useEffect(() => {
    if (hasMore && index >= photos.length - LIGHTBOX_PREFETCH) onNeedMore();
  }, [hasMore, index, photos.length, onNeedMore]);

  const goPrev = useCallback(() => {
    if (index > 0) onNavigate(index - 1);
  }, [index, onNavigate]);

  const goNext = useCallback(() => {
    if (index < photos.length - 1) onNavigate(index + 1);
  }, [index, onNavigate, photos.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  // Swipe no celular: arrastar para a esquerda avanca, para a direita volta.
  const onTouchStart = (e) => {
    const touch = e.changedTouches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close")}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
      >
        <X size={20} />
      </button>

      {index > 0 && (
        <button
          type="button"
          aria-label={t("public.event.previous")}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index - 1);
          }}
          className="absolute left-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      <div
        className="flex max-h-[88vh] flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* A foto grande corre o mesmo risco de 503 da miniatura — mesma
            politica de reenvio, com o botao a mao se as tentativas acabarem. */}
        {full.failed ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/30 px-10 py-16 text-center">
            <ImageOff size={28} className="text-white/40" />
            <span className="text-sm text-white/60">{photo.original_name}</span>
            <button
              type="button"
              onClick={full.retry}
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20"
            >
              <RefreshCw size={15} />
              {t("common.retry")}
            </button>
          </div>
        ) : (
          <img
            src={full.url}
            alt={photo.original_name || ""}
            onError={full.onError}
            className="max-h-[80vh] max-w-full rounded-lg object-contain"
          />
        )}
        <div className="flex items-center gap-2">
          {/* `download` so baixa em vez de navegar porque a foto e servida da
              MESMA origem (/media/...) — o atributo e ignorado cross-origin. */}
          <a
            href={photo.image_url}
            download
            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20"
          >
            <Download size={16} />
            {t("public.photo.download")}
          </a>
          {/* Compartilha a URL canonica /photo/<uuid> — a que o crawler de rede
              social entende (og:image), nao o arquivo cru. */}
          <ShareButton photoId={photo.id} />
          {photo.event_name && (
            <Link
              to={`/events/${photo.event}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20"
            >
              <ExternalLink size={16} />
              {t("public.search.openEvent")}
            </Link>
          )}
        </div>
      </div>

      {/* No fim do lote ja carregado a seta continua visivel enquanto houver
          album a buscar — girando, para nao parecer que a foto acabou. */}
      {(!atEnd || hasMore) && (
        <button
          type="button"
          aria-label={t("public.event.next")}
          disabled={atEnd}
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          className="absolute right-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20 disabled:cursor-default disabled:hover:bg-white/10"
        >
          {atEnd ? (
            <Loader2 size={22} className="animate-spin" />
          ) : (
            <ChevronRight size={22} />
          )}
        </button>
      )}
    </div>
  );
}

export function PhotoGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {Array.from({ length: 15 }).map((_, index) => (
        <div
          key={index}
          className="aspect-square animate-pulse rounded-xl bg-neutral-100"
        />
      ))}
    </div>
  );
}
